import {
  Attributes,
  closeCurrentDialog,
  openDialog,
  showNotification,
  type Action,
} from 'platform/components';
import {Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useRef} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  usePublishModelYearMutation,
  useUnpublishModelYearMutation,
  type ModelYearResponseBody,
} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, parseDate, type ApiError, type Nullish} from 'shared/web';

import {handleCatalogueError} from '../utils/catalogueUi';

const attributeValue = (text: string | number) => (
  <Text size="xSmall" alternative>
    {text}
  </Text>
);

type Params = {
  year: ModelYearResponseBody | Nullish;
  publishableVariants: number | Nullish;
  canPublish: boolean | Nullish;
  isReadinessFetching: boolean;
  isMatrixFetching: boolean;
  hasWarnings: boolean;
  onWarnings: () => void;
  onPublishError: (error: ApiError) => void;
};

export function useModelYearLifecycle({
  year,
  canPublish,
  publishableVariants,
  isReadinessFetching,
  isMatrixFetching,
  hasWarnings,
  onWarnings,
  onPublishError,
}: Params) {
  const formatDate = useDateTimeFormatter();
  const [publish, {isLoading: isPublishing}] = usePublishModelYearMutation();
  const [unpublishYear, {isLoading: isUnpublishing}] = useUnpublishModelYearMutation();
  const isBusy = isPublishing || isUnpublishing;

  // The imperative dialog buttons cannot follow mutation state, so a second click is dropped here
  const isInFlight = useRef(false);
  const once = async (action: () => Promise<unknown>) => {
    if (isInFlight.current) {
      return;
    }
    isInFlight.current = true;
    try {
      await action();
    } finally {
      isInFlight.current = false;
    }
  };

  const publishYear = async () => {
    if (isNil(year)) {
      return;
    }
    await publish({modelYearId: year.id, versionedWriteRequestBody: {version: year.version}})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.newVehicle.catalogue.published'));
      })
      .catch(onPublishError);
  };

  const confirmPublish = () => {
    if (isNil(year)) {
      return;
    }
    const validTo = isNotNil(year.validTo)
      ? formatDate('dateMedium', parseDate(year.validTo))
      : i18n.t('entity.newVehicle.catalogue.openEnded');
    openDialog(
      <VStack spacing={3}>
        <Text size="small" color="secondary">
          {i18n.t('entity.newVehicle.confirmations.publishModelYearLead')}
        </Text>
        <Attributes
          size="quarter"
          rows={[
            {
              label: i18n.t('entity.newVehicle.confirmations.publishModelYearValidity'),
              content: attributeValue(
                `${formatDate('dateMedium', parseDate(year.validFrom))} → ${validTo}`
              ),
            },
            {
              label: i18n.t('entity.newVehicle.catalogue.pricelistVersionLabel'),
              content: attributeValue(year.pricelistVersionLabel),
            },
            {
              label: i18n.t('entity.newVehicle.confirmations.publishModelYearVariants'),
              content: attributeValue(publishableVariants ?? EMPTY_PLACEHOLDER),
            },
          ]}
        />
      </VStack>,
      {
        size: 'default',
        title: i18n.t('entity.newVehicle.confirmations.publishModelYearTitle', {name: year.name}),
        buttons: [
          {
            variant: 'secondary',
            title: i18n.t('general.actions.cancel'),
            onClick: closeCurrentDialog,
          },
          {
            variant: 'primary',
            title: i18n.t('general.actions.confirm'),
            onClick: () => {
              closeCurrentDialog();
              void once(publishYear);
            },
          },
        ],
      }
    );
  };

  const unpublishCurrentYear = () => {
    if (isNil(year)) {
      return;
    }
    openDialog(
      <Text size="small" color="secondary">
        {i18n.t('entity.newVehicle.confirmations.unpublishModelYearBody')}
      </Text>,
      {
        size: 'small',
        title: i18n.t('entity.newVehicle.confirmations.unpublishModelYearTitle', {name: year.name}),
        buttons: [
          {
            variant: 'secondary',
            title: i18n.t('general.actions.cancel'),
            onClick: closeCurrentDialog,
          },
          {
            variant: 'primary',
            title: i18n.t('general.actions.confirm'),
            onClick: () => {
              closeCurrentDialog();
              void once(() =>
                unpublishYear({
                  modelYearId: year.id,
                  versionedWriteRequestBody: {version: year.version},
                })
                  .unwrap()
                  .then(() => {
                    showNotification.success(i18n.t('entity.newVehicle.catalogue.unpublished'));
                  })
                  .catch(handleCatalogueError)
              );
            },
          },
        ],
      }
    );
  };

  const blockedReason = i18n.t('entity.newVehicle.catalogue.unpublishBlockedReason');
  const unpublishTitle = i18n.t('entity.newVehicle.catalogue.unpublish');

  const headerAction: Action | undefined =
    canPublish !== true
      ? undefined
      : match(year)
          .with(
            {status: 'draft'},
            () =>
              ({
                type: 'button',
                title: i18n.t('entity.newVehicle.catalogue.publish'),
                variant: 'primary',
                isLoading: isBusy,
                // Which path Publish takes depends on readiness, so it waits for the current result.
                isDisabled: isBusy || isReadinessFetching || isMatrixFetching,
                onClick: hasWarnings ? onWarnings : confirmPublish,
              }) satisfies Action
          )
          .with(
            {status: 'active', hasConfigurations: true},
            () =>
              ({
                type: 'buttonWithTooltip',
                title: unpublishTitle,
                variant: 'secondary',
                isDisabled: true,
                tooltip: {label: blockedReason},
              }) satisfies Action
          )
          .with(
            {status: 'active'},
            () =>
              ({
                type: 'button',
                title: unpublishTitle,
                variant: 'secondary',
                isLoading: isBusy,
                isDisabled: isBusy,
                onClick: unpublishCurrentYear,
              }) satisfies Action
          )
          .otherwise(() => undefined);

  return {headerAction, confirmPublish};
}
